// Performance Monitoring Utility for CryptoWeb
// Helps optimize component rendering and API efficiency

class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.renderCounts = new Map();
    this.apiCalls = new Map();
    this.memorySnapshots = [];
    this.vitalsData = [];
    this.imageLoadTimes = new Map();
    this.isSupported = typeof performance !== 'undefined';
    this.observers = []; // Track observers for cleanup
  }

  // Monitor component render performance with enhanced tracking
  measureRender(componentName, callback) {
    if (!this.isSupported) return callback();
    
    const startTime = performance.now();
    
    const result = callback();
    
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    this.recordMetric('render', componentName, duration);
    this.incrementRenderCount(componentName);
    
    // Log slow renders (>16ms for 60fps)
    if (duration > 16) {
      console.warn(`🐌 Slow render detected: ${componentName} took ${duration.toFixed(2)}ms`);
    }
    
    return result;
  }

  // Enhanced API call monitoring with retry tracking
  async measureApiCall(apiName, apiCall) {
    if (!this.isSupported) return await apiCall();
    
    const startTime = performance.now();
    
    try {
      const result = await apiCall();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.recordMetric('api', apiName, duration);
      this.recordApiCall(apiName, 'success', duration);
      
      // Log slow API calls
      if (duration > 3000) {
        console.warn(`🌐 Slow API call: ${apiName} took ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      this.recordApiCall(apiName, 'error', duration);
      throw error;
    }
  }

  // Monitor image loading performance
  measureImageLoad(imageSrc, onLoad) {
    if (!this.isSupported) return onLoad();
    
    const startTime = performance.now();
    const img = new Image();
    
    img.onload = () => {
      const duration = performance.now() - startTime;
      this.imageLoadTimes.set(imageSrc, duration);
      
      if (duration > 2000) {
        console.warn(`🖼️ Slow image load: ${imageSrc} took ${duration.toFixed(2)}ms`);
      }
      
      onLoad();
    };
    
    img.onerror = onLoad;
    img.src = imageSrc;
  }

  // Track Core Web Vitals
  trackWebVitals() {
    if (!this.isSupported) return;
    
    // Track Largest Contentful Paint (LCP)
    this.observeWebVital('largest-contentful-paint', (entry) => {
      this.vitalsData.push({
        name: 'LCP',
        value: entry.startTime,
        rating: entry.startTime <= 2500 ? 'good' : entry.startTime <= 4000 ? 'needs-improvement' : 'poor'
      });
    });

    // Track First Input Delay (FID)
    this.observeWebVital('first-input', (entry) => {
      this.vitalsData.push({
        name: 'FID',
        value: entry.processingStart - entry.startTime,
        rating: entry.processingStart - entry.startTime <= 100 ? 'good' : 
                entry.processingStart - entry.startTime <= 300 ? 'needs-improvement' : 'poor'
      });
    });

    // Track Cumulative Layout Shift (CLS)
    this.observeWebVital('layout-shift', (entry) => {
      if (!entry.hadRecentInput) {
        this.vitalsData.push({
          name: 'CLS',
          value: entry.value,
          rating: entry.value <= 0.1 ? 'good' : entry.value <= 0.25 ? 'needs-improvement' : 'poor'
        });
      }
    });
  }

  // Helper function to observe web vitals
  observeWebVital(type, callback) {
    try {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          callback(entry);
        }
      });
      observer.observe({ type, buffered: true });
      this.observers.push(observer); // Track for cleanup
    } catch (e) {
      // PerformanceObserver not supported
    }
  }

  // Cleanup method to disconnect all observers
  cleanup() {
    this.observers.forEach(observer => {
      try {
        observer.disconnect();
      } catch (e) {
        // Observer already disconnected
      }
    });
    this.observers = [];
  }

  // Get current memory usage
  getMemoryUsage() {
    if (performance.memory) {
      return {
        usedJSHeapSize: performance.memory.usedJSHeapSize,
        totalJSHeapSize: performance.memory.totalJSHeapSize,
        jsHeapSizeLimit: performance.memory.jsHeapSizeLimit
      };
    }
    return null;
  }

  // Take memory snapshot
  takeMemorySnapshot(label = 'snapshot') {
    const memory = this.getMemoryUsage();
    if (memory) {
      this.memorySnapshots.push({
        label,
        timestamp: Date.now(),
        ...memory
      });
    }
  }

  // Detect memory leaks
  detectMemoryLeaks() {
    if (this.memorySnapshots.length < 2) return null;
    
    const recent = this.memorySnapshots.slice(-5);
    const isLeaking = recent.every((snapshot, index) => {
      if (index === 0) return true;
      return snapshot.usedJSHeapSize > recent[index - 1].usedJSHeapSize;
    });
    
    if (isLeaking) {
      const growth = recent[recent.length - 1].usedJSHeapSize - recent[0].usedJSHeapSize;
      console.warn(`🚨 Potential memory leak detected! Growth: ${(growth / 1024 / 1024).toFixed(2)}MB`);
      return { growth, snapshots: recent };
    }
    
    return null;
  }

  // Record metric
  recordMetric(type, name, duration) {
    const key = `${type}-${name}`;
    if (!this.metrics.has(key)) {
      this.metrics.set(key, []);
    }
    this.metrics.get(key).push(duration);
  }

  // Track render counts
  incrementRenderCount(componentName) {
    const count = this.renderCounts.get(componentName) || 0;
    this.renderCounts.set(componentName, count + 1);
    
    // Warn about excessive re-renders
    if (count > 0 && count % 10 === 0) {
      console.warn(`High render count: ${componentName} has rendered ${count} times`);
    }
  }

  // Track API calls
  recordApiCall(apiName, status, duration) {
    const key = `${apiName}-${status}`;
    if (!this.apiCalls.has(key)) {
      this.apiCalls.set(key, { count: 0, totalDuration: 0 });
    }
    
    const stats = this.apiCalls.get(key);
    stats.count++;
    stats.totalDuration += duration;
    
    this.apiCalls.set(key, stats);
  }

  // Get enhanced performance report
  getReport() {
    const report = {
      renders: {},
      apis: {},
      renderCounts: Object.fromEntries(this.renderCounts),
      webVitals: this.vitalsData,
      imageLoads: Object.fromEntries(this.imageLoadTimes),
      memorySnapshots: this.memorySnapshots,
      memoryLeak: this.detectMemoryLeaks(),
      summary: this.getSummary()
    };

    // Process render metrics
    for (const [key, durations] of this.metrics) {
      if (key.startsWith('render-')) {
        const componentName = key.replace('render-', '');
        report.renders[componentName] = this.calculateStats(durations);
      }
    }

    // Process API metrics
    for (const [key, durations] of this.metrics) {
      if (key.startsWith('api-')) {
        const apiName = key.replace('api-', '');
        report.apis[apiName] = this.calculateStats(durations);
      }
    }

    return report;
  }

  // Enhanced performance summary
  getSummary() {
    const totalRenders = Array.from(this.renderCounts.values()).reduce((a, b) => a + b, 0);
    const totalApiCalls = Array.from(this.apiCalls.values()).reduce((total, stats) => total + stats.count, 0);
    const avgImageLoadTime = this.imageLoadTimes.size > 0 ? 
      Array.from(this.imageLoadTimes.values()).reduce((a, b) => a + b, 0) / this.imageLoadTimes.size : 0;
    
    const currentMemory = this.getMemoryUsage();
    const memoryUsagePercent = currentMemory ? 
      (currentMemory.usedJSHeapSize / currentMemory.jsHeapSizeLimit * 100).toFixed(2) : 0;
    
    return {
      totalRenders,
      totalApiCalls,
      componentsTracked: this.renderCounts.size,
      apisTracked: new Set(Array.from(this.apiCalls.keys()).map(key => key.split('-')[0])).size,
      avgImageLoadTime: avgImageLoadTime.toFixed(2),
      imagesLoaded: this.imageLoadTimes.size,
      memoryUsagePercent,
      memorySnapshots: this.memorySnapshots.length,
      webVitalsRecorded: this.vitalsData.length
    };
  }

  // Enhanced performance report logging
  logReport() {
    const report = this.getReport();
    console.group('🚀 CryptoWeb Performance Report');
    
    console.group('📊 Summary');
    console.table(report.summary);
    console.groupEnd();
    
    if (Object.keys(report.renders).length > 0) {
      console.group('🎨 Component Renders');
      console.table(report.renders);
      console.groupEnd();
    }
    
    if (Object.keys(report.apis).length > 0) {
      console.group('🌐 API Calls');
      console.table(report.apis);
      console.groupEnd();
    }
    
    if (report.webVitals.length > 0) {
      console.group('📈 Core Web Vitals');
      console.table(report.webVitals);
      console.groupEnd();
    }
    
    if (Object.keys(report.imageLoads).length > 0) {
      console.group('�️ Image Load Times');
      console.table(report.imageLoads);
      console.groupEnd();
    }
    
    if (report.memoryLeak) {
      console.group('🚨 Memory Leak Detection');
      console.warn('Potential memory leak detected:', report.memoryLeak);
      console.groupEnd();
    }
    
    console.groupEnd();
    
    // Take a memory snapshot after each report
    this.takeMemorySnapshot('report');
  }

  // Calculate statistics for an array of durations
  calculateStats(durations) {
    if (durations.length === 0) return null;
    
    const sorted = [...durations].sort((a, b) => a - b);
    const sum = durations.reduce((a, b) => a + b, 0);
    
    return {
      count: durations.length,
      min: Math.min(...durations),
      max: Math.max(...durations),
      avg: sum / durations.length,
      median: sorted[Math.floor(sorted.length / 2)],
      p95: sorted[Math.floor(sorted.length * 0.95)]
    };
  }

  // Optimize images with lazy loading
  optimizeImage(imgElement, options = {}) {
    const {
      threshold = 0.1,
      rootMargin = '50px'
    } = options;

    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target;
            const startTime = performance.now();
            
            img.src = img.dataset.src;
            img.classList.remove('lazy');
            
            img.onload = () => {
              const loadTime = performance.now() - startTime;
              this.imageLoadTimes.set(img.dataset.src, loadTime);
              img.classList.add('loaded');
            };
            
            observer.unobserve(img);
          }
        });
      }, { threshold, rootMargin });

      imageObserver.observe(imgElement);
    } else {
      // Fallback for browsers without IntersectionObserver
      imgElement.src = imgElement.dataset.src;
    }
  }

  // Debounce function for performance optimization
  debounce(func, wait, immediate = false) {
    let timeout;
    const debounced = function executedFunction(...args) {
      const later = () => {
        timeout = null;
        if (!immediate) func(...args);
      };
      const callNow = immediate && !timeout;
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
      if (callNow) func(...args);
    };
    
    // Add cancel method to clear timeout
    debounced.cancel = function() {
      clearTimeout(timeout);
      timeout = null;
    };
    
    return debounced;
  }

  // Throttle function for performance optimization
  throttle(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }
}

// Create global instance
const perfMonitor = new PerformanceMonitor();

// Initialize Web Vitals tracking
if (typeof window !== 'undefined') {
  perfMonitor.trackWebVitals();
  perfMonitor.takeMemorySnapshot('initial');
}

// Export for use in components
export default perfMonitor;

// Export cleanup function
export const cleanupPerformanceMonitor = () => {
  perfMonitor.cleanup();
  if (autoLogInterval) {
    clearInterval(autoLogInterval);
    autoLogInterval = null;
  }
};

// Helper hooks for React components
export const usePerformanceMonitor = () => {
  return {
    measureRender: perfMonitor.measureRender.bind(perfMonitor),
    measureApiCall: perfMonitor.measureApiCall.bind(perfMonitor),
    getReport: perfMonitor.getReport.bind(perfMonitor),
    logReport: perfMonitor.logReport.bind(perfMonitor),
    takeMemorySnapshot: perfMonitor.takeMemorySnapshot.bind(perfMonitor),
    optimizeImage: perfMonitor.optimizeImage.bind(perfMonitor),
    debounce: perfMonitor.debounce.bind(perfMonitor),
    throttle: perfMonitor.throttle.bind(perfMonitor)
  };
};

// Memory usage monitor with enhanced tracking
export const monitorMemory = () => {
  if (performance.memory) {
    const memory = performance.memory;
    const usage = {
      used: `${(memory.usedJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      total: `${(memory.totalJSHeapSize / 1024 / 1024).toFixed(2)} MB`,
      limit: `${(memory.jsHeapSizeLimit / 1024 / 1024).toFixed(2)} MB`,
      usage: `${((memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100).toFixed(2)}%`
    };
    
    console.log('💾 Memory Usage:', usage);
    
    // Warn if memory usage is high
    if ((memory.usedJSHeapSize / memory.jsHeapSizeLimit) > 0.8) {
      console.warn('⚠️ High memory usage detected! Consider optimizing.');
    }
    
    return usage;
  }
  return null;
};

// Resource timing analyzer
export const analyzeResourceTiming = () => {
  if (!performance.getEntriesByType) return null;
  
  const resources = performance.getEntriesByType('resource');
  const analysis = {
    scripts: [],
    styles: [],
    images: [],
    fonts: [],
    other: []
  };
  
  resources.forEach(resource => {
    const type = resource.name.includes('.js') ? 'scripts' :
                 resource.name.includes('.css') ? 'styles' :
                 resource.name.match(/\.(jpg|jpeg|png|gif|svg|webp)/) ? 'images' :
                 resource.name.match(/\.(woff|woff2|ttf|eot)/) ? 'fonts' : 'other';
    
    analysis[type].push({
      name: resource.name.split('/').pop(),
      duration: resource.duration,
      size: resource.transferSize,
      compressed: resource.encodedBodySize,
      uncompressed: resource.decodedBodySize
    });
  });
  
  console.group('📊 Resource Loading Analysis');
  Object.entries(analysis).forEach(([type, resources]) => {
    if (resources.length > 0) {
      console.group(`${type.toUpperCase()} (${resources.length})`);
      console.table(resources);
      console.groupEnd();
    }
  });
  console.groupEnd();
  
  return analysis;
};

// Auto-log performance report every 30 seconds in development with enhanced monitoring
let autoLogInterval = null;
if (process.env.NODE_ENV === 'development') {
  autoLogInterval = setInterval(() => {
    perfMonitor.logReport();
    monitorMemory();
    
    // Analyze resources every 5 minutes
    if (Date.now() % (5 * 60 * 1000) < 30000) {
      analyzeResourceTiming();
    }
  }, 30000);
}

// Cleanup on page unload to prevent memory leaks
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    perfMonitor.cleanup();
    if (autoLogInterval) {
      clearInterval(autoLogInterval);
    }
  });
}